import React, { useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormLabel,
  HStack,
  Select,
  SimpleGrid,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { client } from '@php-beam/packages/frontend/src/api/client';
import { route } from '@php-beam/packages/frontend/src/hooks/useRouter';

const CARRIERS = [
  { value: 'peopaygo', label: 'PeoPAYGO' },
  { value: 'ocmi', label: 'OCMI' },
  { value: 'compeo', label: 'Compeo' },
  { value: 'cornerstone', label: 'Cornerstone' },
  { value: 'coguard', label: 'CoGuard' },
  { value: 'signature', label: 'Signature' },
  { value: 'enterprisehr', label: 'EnterpriseHR' },
  { value: 'frankcrum', label: 'FrankCrum' },
  { value: 'southeast', label: 'Southeast' },
];

export default function ApprovalAction({
  submissionId,
}: {
  submissionId: number | null;
}) {
  const [processing, setProcessing] = useState(false);
  const [editing, setEditing] = useState(false);
  const [carrier, setCarrier] = useState('peopaygo');
  const [charge, setCharge] = useState(false);
  const [offline, setOffline] = useState(true);
  const toast = useToast();

  const approve = async () => {
    if (!submissionId) return;
    setProcessing(true);
    try {
      await client.post(
        route('customers.underwriting.approve', {
          lead_submission: submissionId,
        }),
        { carrier, charge, offline },
      );
      toast({
        title: `Submission #${submissionId} approved`,
        description: `Carrier: ${carrier}, ${charge ? 'charging initial cost' : 'no charge'}${offline ? ', offline payment' : ''}`,
        status: 'success',
        duration: 3000,
      });
    } catch (err: any) {
      const errors = err?.response?.data?.errors;
      const message =
        err?.response?.data?.message ||
        (errors ? Object.values(errors).flat().join(', ') : 'Unknown error');
      toast({ title: 'Approval failed', description: message, status: 'error' });
    } finally {
      setProcessing(false);
    }
  };

  const reject = async () => {
    if (!submissionId) return;
    setProcessing(true);
    try {
      await client.post(
        route('customers.underwriting.reject', {
          lead_submission: submissionId,
          note: 'DevTools rejection',
        }),
      );
      toast({
        title: `Submission #${submissionId} rejected`,
        status: 'info',
        duration: 3000,
      });
    } catch (err: any) {
      const errors = err?.response?.data?.errors;
      const message =
        err?.response?.data?.message ||
        (errors ? Object.values(errors).flat().join(', ') : 'Unknown error');
      toast({ title: 'Rejection failed', description: message, status: 'error' });
    } finally {
      setProcessing(false);
    }
  };

  return (
    <VStack align="start" spacing={3}>
      <HStack spacing={2}>
        <Button
          colorScheme="green"
          size="sm"
          onClick={approve}
          isLoading={processing}
          isDisabled={!submissionId}
        >
          Quick Approve #{submissionId}
        </Button>
        <Button
          colorScheme="red"
          variant="outline"
          size="sm"
          onClick={reject}
          isLoading={processing}
          isDisabled={!submissionId}
        >
          Reject #{submissionId}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setEditing(!editing)}
        >
          {editing ? 'Hide Options' : 'Edit Defaults'}
        </Button>
      </HStack>

      <Text fontSize="xs" color="gray.500">
        Defaults: {carrier.toUpperCase()}, {offline ? 'offline payment' : charge ? 'charge initial cost' : 'no charge'}
      </Text>

      {editing && (
        <Box p={3} borderWidth={1} borderRadius="md" borderColor="gray.200" w="full">
          <SimpleGrid columns={2} spacing={3}>
            <FormControl>
              <FormLabel fontSize="xs">Carrier</FormLabel>
              <Select
                size="sm"
                value={carrier}
                onChange={(e) => setCarrier(e.target.value)}
              >
                {CARRIERS.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </Select>
            </FormControl>

            <VStack align="start" spacing={2} pt={6}>
              <Checkbox
                size="sm"
                isChecked={charge}
                isDisabled={offline}
                onChange={(e) => setCharge(e.target.checked)}
              >
                Charge initial cost
              </Checkbox>
              <Checkbox
                size="sm"
                isChecked={offline}
                onChange={(e) => {
                  setOffline(e.target.checked);
                  if (e.target.checked) setCharge(false);
                }}
              >
                Offline payment (no transaction)
              </Checkbox>
            </VStack>
          </SimpleGrid>
        </Box>
      )}
    </VStack>
  );
}
